/**
 * 学习成果（TEACH-PAGES-117；方案 9.1「学习成果」：本宠动作预览、启用控制）。三张卡：标题、预览、学会以后。
 * - 准备好了：主按钮「允许 TA 在合适的时候用」——预览还没看成时不能按，说清先看过再决定（设计评审第一轮中 19）；
 *   三段没齐说动作还在生效，读失败说先点「重新看看」（6c2b 轻审高 2）；
 *   可以用了：「先不让 TA 用」；先不用了：「让 TA 再用起来」（adm1：启用、停用都不动次数）。三种是同一个键换字，按完焦点留在原处（评审高 6）；
 *   请求中、预览没到时只标 aria-disabled，不真禁用（真禁用会把焦点丢到 body，评审第二轮高 1）。
 * - 删掉：先问一句，明说删了 TA 就不会再用、不会退回学习次数（adm1 交代给页面的；评审中 11）；删掉以后回列表。
 * - 照新样子准备中：只说那一句，不放预览和开关（旧动作这时不算可用）。
 * - 免费微调这一版不放：后端不收「要调什么」，按下去就用掉唯一一次（评审高 3），等 adm1 收调整内容再加（README 后续）。
 * - 反馈入口：后端还没有收反馈的口子，这一版不放（README 后续）。
 * - 不是自己发起的单（家人看）：只看，不放开关和删除。
 */
import { useRef, useState } from "react";
import { useNavigate } from "react-router";
import { toApiError } from "@/shared/api/errors";
import { DELETE, DELETE_WARNING, DELETE_YES, DISABLE, ENABLE_AGAIN, ENABLE_FIRST, PREVIEW_FAILED_WAIT, PREVIEW_NOT_YET, canPreview, stateCopy } from "./teachModel";
import type { TeachOrder } from "./teachTypes";
import { useTeachAction } from "./teachActions";
import { TeachPreview, type PreviewStatus } from "./TeachPreview";
import { TeachAsk } from "./TeachAsk";
import { TeachButton } from "./TeachButton";
import { OrderHeader, SpecSummary, type OrderPet } from "./TeachOrderPage";

export function TeachResult({ order, pet }: { order: TeachOrder; pet: OrderPet }) {
  const navigate = useNavigate();
  const [asking, setAsking] = useState(false);
  const [previewStatus, setPreviewStatus] = useState<PreviewStatus>("pending");
  const deleteButton = useRef<HTMLButtonElement>(null);
  const toggle = useTeachAction<boolean>(order, "enable", (api, key, on) => api.setEnabled(order.order_id, on, key));
  const remove = useTeachAction<null>(order, "delete", (api, key) => api.remove(order.order_id, key), () => navigate("/me/moves", { replace: true }));
  const copy = stateCopy(order.state);
  const spec = order.confirmed_spec ?? order.spec ?? null;
  const preview = canPreview(order.state);
  const error = toggle.error ?? remove.error;
  const turnOn = order.state !== "enabled";
  const label = order.state === "available" ? ENABLE_FIRST : order.state === "disabled" ? ENABLE_AGAIN : DISABLE;
  const waitPreview = order.state === "available" && previewStatus !== "ready";
  // 正在读时只是不能按，不另说话；三段没齐、读失败各说各的
  const waitWhy = previewStatus === "failed" ? PREVIEW_FAILED_WAIT : previewStatus === "missing" ? PREVIEW_NOT_YET : null;
  return (
    <>
      <OrderHeader order={order} pet={pet} />
      {preview ? <TeachPreview orderId={order.order_id} state={order.state} pet={pet} onStatus={setPreviewStatus} /> : null}
      <section className="ps-teach-card" aria-label="学会以后">
        {copy ? <p className="ps-teach-lead" role="status">{copy.line}</p> : null}
        {spec ? <SpecSummary steps={spec.steps} /> : null}
        {error ? <p className="ps-teach-form__error" role="alert">{toApiError(error).playerMessage}</p> : null}
        {order.mine && preview ? (
          <div className="ps-teach-actions">
            {waitPreview && waitWhy ? <p className="ps-teach-note" id="teach-wait-preview">{waitWhy}</p> : null}
            <TeachButton
              variant={turnOn ? "primary" : "secondary"}
              block
              busy={toggle.isPending}
              unavailable={waitPreview}
              describedBy={waitPreview && waitWhy ? "teach-wait-preview" : undefined}
              onClick={() => toggle.mutate(turnOn)}
              data-teach-toggle
            >
              {label}
            </TeachButton>
            {asking ? (
              <TeachAsk label={DELETE} text={DELETE_WARNING} yes={DELETE_YES} busy={remove.isPending} onYes={() => remove.mutate(null)} onNo={() => setAsking(false)} returnTo={deleteButton} />
            ) : (
              <TeachButton buttonRef={deleteButton} variant="ghost" className="ps-teach-delete" unavailable={toggle.isPending} onClick={() => setAsking(true)}>{DELETE}</TeachButton>
            )}
          </div>
        ) : null}
      </section>
    </>
  );
}
